import React from 'react'
import { useParams } from 'react-router-dom'
import { CompletionStatus, CreditApplicationBankSource, SectionName } from '_gql'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage } from '@divvy-web/i18n'
import Drawer, { DrawerView } from '@divvy-web/skylab.drawer'
import Scrim from '@divvy-web/skylab.scrim'
import { TOAST_TYPE_DANGER, TOAST_TYPE_SUCCESS, useToast } from '@divvy-web/skylab.toast'

import useApplicantTokenOnMount from '@sputnik/hooks/useApplicantTokenOnMount'
import useDivvyUUID from '@sputnik/hooks/useDivvyUUID'
import {
  GetBankAccountInfo,
  GetBankAccountInfoDocument,
  GetBankAccountInfoVariables,
} from '@sputnik/pages/CreditApplicationPages/ConnectBankAccountForm/GetBankAccountInfo.gql'
import { useMXConnectionStatus } from '@sputnik/pages/CreditApplicationPages/ConnectBankAccountForm/MXConnectionStatusProvider'
import { useGetApplicationProgressLazyQuery } from '@sputnik/pages/CreditApplicationPages/Navigation/GetApplicationProgress.gql'
import { useApplicationProgress } from '@sputnik/pages/CreditApplicationPages/Navigation/useApplicationProgress'
import { useWebTracking } from '@sputnik/tracking/WebTrackingContext'

import MoneyMoverMXWidget from './MoneyMoverMXWidget'
import styles from './styles.module.scss'
import { useSyncBankConnections } from './SyncBankConnections.gql'

export interface AddMXBankAccountDrawerProps {
  isShowing: boolean
  onClose: () => void
}

const AddMXBankAccountDrawer = ({ isShowing, onClose }: AddMXBankAccountDrawerProps) => {
  const [getClassName] = useNameFormatter('AddMXBankAccountDrawer')
  const { appId } = useParams()
  const [companyDivvyUuid] = useDivvyUUID(appId ?? '')
  const showToast = useToast()
  const { setNewBankAccountId } = useMXConnectionStatus()
  const { tracker } = useWebTracking()
  const prevCompletionStatus = useApplicationProgress({ sectionName: SectionName.FINANCE_INFO })
  const [getApplicationProgress] = useGetApplicationProgressLazyQuery({
    variables: { creditApplicationId: appId ?? '' },
  })

  useApplicantTokenOnMount({ salesforceCreditId: appId ?? '' })

  const [syncBankConnections, { client }] = useSyncBankConnections()

  const onBankAccountConnected = (connectionGuid: string, userGuid: string) => {
    syncBankConnections({
      variables: {
        connectionGuid,
        userGuid,
        divvyUuid: companyDivvyUuid ?? '',
      },
      onError: () => {
        showToast(
          TOAST_TYPE_DANGER,
          <FormattedMessage
            defaultMessage='Error connecting bank account. Please try again.'
            id='sputnik.AddMXBankAccountDrawer__L6akzs'
          />,
          { className: 'fs-unmask' },
        )
      },
      onCompleted: async (result) => {
        // tracking for Connect bank section and finance info section as a whole

        tracker?.track('se_signup__application_financial_info_bank_connection__succeeded')

        const { data: updatedCompletionStatus } = await getApplicationProgress()

        const isBankConnectionInfoSectionComplete = updatedCompletionStatus?.creditApplication?.savedSectionsInfo?.find(
          (section) =>
            section?.sectionName === SectionName.FINANCE_INFO &&
            section?.completionStatus === CompletionStatus.COMPLETED,
        )

        if (isBankConnectionInfoSectionComplete && prevCompletionStatus !== CompletionStatus.COMPLETED) {
          tracker?.track('se_signup__application_financial_info_bank_connection_section_progress__completed', {
            is_complete: true,
          })
        }

        if (!result.connection?.bankAccounts) {
          return
        }

        const newBankAccount = result.connection.bankAccounts[0]
        if (!newBankAccount?.id) {
          return
        }

        setNewBankAccountId(newBankAccount.id)

        client.cache.updateQuery<GetBankAccountInfo, GetBankAccountInfoVariables>(
          {
            query: GetBankAccountInfoDocument,
            variables: { creditApplicationId: appId ?? '' },
          },
          (data) => {
            if (!data?.creditApplication?.financeInfo?.externalBankOptions) {
              return data
            }

            if (
              data.creditApplication.financeInfo.externalBankOptions.some((option) => option?.id === newBankAccount?.id)
            ) {
              return data
            }

            return {
              ...data,
              creditApplication: {
                ...data.creditApplication,
                financeInfo: {
                  ...data.creditApplication.financeInfo,
                  externalBankOptions: [
                    ...data.creditApplication.financeInfo.externalBankOptions,
                    {
                      __typename: 'BankInfo',
                      accountHolderName: newBankAccount?.accountHolderName ?? '',
                      accountNumberLastFour: newBankAccount?.bankAccountNumberLastFour ?? '',
                      accountNumberLength: newBankAccount?.accountNumberLength ?? '',
                      bankName: newBankAccount?.institutionName ?? '',
                      id: newBankAccount?.id ?? '',
                      routingNumber: newBankAccount?.bankRoutingNumber ?? '',
                      source: CreditApplicationBankSource.MONEY_MOVER,
                      externalConnectionGuid: connectionGuid,
                      logoUrl: result.connection?.institutionMediumLogoUrl ?? '',
                    },
                  ],
                },
              },
            }
          },
        )

        showToast(
          TOAST_TYPE_SUCCESS,
          <FormattedMessage
            defaultMessage='Bank account successfully linked'
            id='sputnik.AddMXBankAccountDrawer__XnA/5h'
          />,
          { autoHideDelay: 3000 },
        )
      },
    })
  }

  return (
    <Scrim isShowing={isShowing}>
      <Drawer
        activeViewKey='mxConnection'
        className={styles[getClassName('')]}
        dataTestId='add-mx-bank-account'
        isShowing={isShowing}
        views={{
          mxConnection: (
            <DrawerView
              headerLeftIcon='close'
              headerLeftIconName='close'
              onLeftIconClick={onClose}
            >
              <MoneyMoverMXWidget
                isShowing
                onBankAccountConnected={onBankAccountConnected}
                onClose={onClose}
              />
            </DrawerView>
          ),
        }}
      />
    </Scrim>
  )
}

export default AddMXBankAccountDrawer
